import {
  createContext,
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSessionStorage } from "usehooks-ts";
import { RemiErrorDeprecated } from "@remi/error";
import type { Definition, SortDefinition } from "@remi/types/query-builder";

import { DefaultNotificationsDefinition } from "~/hooks/notifications/useNotifications";

const INBOX_VISIBLE_STORAGE_KEY = "notification-inbox-visible";

type NotificationsContextValue = {
  selectedNotifications: Set<string>;
  setSelectedNotifications: Dispatch<SetStateAction<Set<string>>>;
  activeNotificationId: string | null;
  setActiveNotificationId: Dispatch<SetStateAction<string | null>>;
  lastSelectedIndex: number | null;
  setLastSelectedIndex: Dispatch<SetStateAction<number | null>>;
  notificationDefinition: { definition: Definition; sort: SortDefinition };
  setNotificationDefinition: Dispatch<SetStateAction<{ definition: Definition; sort: SortDefinition }>>;
  inboxVisible: boolean;
  setInboxVisible: Dispatch<SetStateAction<boolean>>;
  inboxRef: RefObject<HTMLElement | null>;
  bellRef: RefObject<HTMLButtonElement | null>;
  // Set by a sidebar link to close the follow-column once its navigation lands, instead of
  // collapsing it in place — otherwise leaving /notifications flashes a collapsed panel mid-nav.
  closeInboxOnNavigateRef: RefObject<boolean>;
};

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function useNotificationsContext(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new RemiErrorDeprecated({
      message: "useNotificationsContext must be used within NotificationsProvider",
    });
  }
  return context;
}

export const useInboxVisible = (): readonly [boolean, Dispatch<SetStateAction<boolean>>] => {
  const ctx = useNotificationsContext();
  return [ctx.inboxVisible, ctx.setInboxVisible] as const;
};

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set());
  const [activeNotificationId, setActiveNotificationId] = useState<string | null>(null);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [notificationDefinition, setNotificationDefinition] = useState<{
    definition: Definition;
    sort: SortDefinition;
  }>(DefaultNotificationsDefinition);
  const [inboxVisible, setInboxVisible] = useSessionStorage<boolean>(INBOX_VISIBLE_STORAGE_KEY, false, {
    initializeWithValue: false,
  });
  const inboxRef = useRef<HTMLElement | null>(null);
  const bellRef = useRef<HTMLButtonElement | null>(null);
  const closeInboxOnNavigateRef = useRef(false);

  const value = useMemo(
    () => ({
      selectedNotifications,
      setSelectedNotifications,
      activeNotificationId,
      setActiveNotificationId,
      lastSelectedIndex,
      setLastSelectedIndex,
      notificationDefinition,
      setNotificationDefinition,
      inboxVisible,
      setInboxVisible,
      inboxRef,
      bellRef,
      closeInboxOnNavigateRef,
    }),
    [
      selectedNotifications,
      activeNotificationId,
      lastSelectedIndex,
      notificationDefinition,
      inboxVisible,
      setInboxVisible,
    ],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}
